// Persistent "active run" session shared across the app so the user can
// navigate away from /run/live (or briefly lose foreground) and pick up
// where they left off. Stored in localStorage so it survives page reloads
// and browser suspension (which happens on mobile when the tab is
// backgrounded).
//
// Shape:
//   {
//     type: 'personal' | 'race',
//     eventId?: string,           // only for race
//     eventName?: string,         // only for race, for banner display
//     startedAt: number,          // ms epoch when running clock started
//     pausedAt: number | null,    // ms epoch of pause start, if paused
//     accumulatedPauseMs: number, // total paused-time to subtract from elapsed
//     distanceMeters: number,
//     path: Array<{lat,lng,t,acc}>,
//     lastUpdate: number,         // ms epoch, for stale-session detection
//   }

const KEY = 'rivl:active-run';
const MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours — auto-purge stale sessions

export function loadRunSession() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== 'object') return null;
    if (Date.now() - (s.lastUpdate || s.startedAt || 0) > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveRunSession(session) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...session, lastUpdate: Date.now() }));
  } catch {
    // Storage full or private-mode — best-effort only.
  }
}

export function clearRunSession() {
  try { localStorage.removeItem(KEY); } catch {}
}

// Wall-clock elapsed seconds, honoring pause state.
export function elapsedSec(session, nowMs = Date.now()) {
  if (!session || !session.startedAt) return 0;
  const paused = session.pausedAt != null;
  const end = paused ? session.pausedAt : nowMs;
  const raw = end - session.startedAt - (session.accumulatedPauseMs || 0);
  return Math.max(0, Math.floor(raw / 1000));
}
